import PubSub from "pubsub-js";
import { pubsubEvents } from "../../components/helpers/socket/pubsubEvent";
import { getFieldLabel, isPriceField } from "../alertUtils";

/**
 * This is the handler for received socket
 * messages from the alerting service.
 *
 * It is intended to match received actions to a dispatch event.
 */
export function alertingEventReciever(data) {
  if (
    data.service === "alerting" &&
    data.action === "notification" &&
    data.payload
  ) {
    try {
      const payloadObject =
        typeof data.payload === "string"
          ? JSON.parse(data.payload)
          : data.payload;
      const alertName = payloadObject.alertName?.replace(/_/g, " ");

      const getOperatorSymbol = (op) => {
        // Handle both Proto Enum values (0-4) and potential string names
        if (typeof op === "string") {
          switch (op) {
            case "GTE":
              return ">=";
            case "GT":
              return ">";
            case "EQ":
              return "=";
            case "LTE":
              return "<=";
            case "LT":
              return "<";
            default:
              return op;
          }
        }
        switch (op) {
          case 0:
            return ">=";
          case 1:
            return ">";
          case 2:
            return "=";
          case 3:
            return "<=";
          case 4:
            return "<";
          // Legacy cases
          case 5:
            return "=";
          default:
            return "";
        }
      };

      let toastMessage = null;
      const matchedConditions = payloadObject.matchedConditions || [];

      if (matchedConditions.length > 0) {
        // Filter for the condition that actually triggered the alert
        const matched = matchedConditions.filter(
          (c) => c.isMatched || c.triggeredValue !== undefined,
        );
        const firstCondition =
          matched.length > 0 ? matched[0] : matchedConditions[0];

        let instrumentName = payloadObject.customData1;
        if (!instrumentName || instrumentName === null) {
          instrumentName =
            firstCondition.instrumentSymbol ||
            payloadObject.symbol ||
            "Instrument";
        }

        const op =
          firstCondition.operatorValue !== undefined
            ? firstCondition.operatorValue
            : firstCondition.operator !== undefined
              ? firstCondition.operator
              : payloadObject.operator !== undefined
                ? payloadObject.operator
                : "GTE";

        const opSymbol = getOperatorSymbol(op);

        let limitValue = firstCondition.limitValue;
        if (limitValue === undefined) limitValue = payloadObject.limitValue;
        if (limitValue === undefined) limitValue = "[Target]";

        const fieldName =
          firstCondition.alertField ||
          firstCondition.field ||
          payloadObject.field;

        let triggeredValue = firstCondition.triggeredValue;
        if (triggeredValue === undefined) {
          if (fieldName && payloadObject[fieldName] !== undefined)
            triggeredValue = payloadObject[fieldName];
        }
        if (triggeredValue === undefined) triggeredValue = "[Value]";

        let readableField = "";
        if (fieldName === "bidPrice") readableField = "bid price";
        else if (fieldName === "bidSize" || fieldName === "bidPriceQty")
          readableField = "bid qty";
        else if (fieldName === "offerPrice") readableField = "offer price";
        else if (fieldName === "offerSize" || fieldName === "offerPriceQty")
          readableField = "offer qty";
        else if (fieldName === "totalVolume" || fieldName === "tradeVolume")
          readableField = "volume";
        else
          readableField = fieldName
            ? getFieldLabel(fieldName).toLowerCase()
            : "price";

        let title = "";
        let body = "";

        if (fieldName === "tradePrice" || !fieldName) {
          title = `${instrumentName} traded at price ${triggeredValue}`;
        } else if (fieldName === "tradePriceQty") {
          title = `${instrumentName} traded with quantity ${triggeredValue}`;
        } else if (fieldName === "totalVolume" || fieldName === "tradeVolume") {
          title = `${instrumentName} total ${readableField} is ${triggeredValue}`;
        } else {
          title = `${instrumentName} ${readableField} is ${triggeredValue}`;
        }

        if (opSymbol === ">" || opSymbol === ">=") {
          body = `Above your target of ${limitValue}`;
        } else if (opSymbol === "<" || opSymbol === "<=") {
          body = `Below your target of ${limitValue}`;
        } else if (opSymbol === "=") {
          body = `Equals your target of ${limitValue}`;
        } else {
          body = `Reached your target of ${limitValue}`;
        }

        toastMessage = `${title}\n${body}`;
      } else {
        // Fallback for old/legacy structure
        const {
          tradePrice,
          tradePriceQty,
          field,
          operator,
          limitValue,
          totalVolume,
          bidPrice,
          offerPrice,
          bidSize,
          offerSize,
        } = payloadObject;
        if (alertName) {
          let resolvedField = field;
          if (!resolvedField) {
            if (tradePrice !== undefined) resolvedField = "tradePrice";
            else if (tradePriceQty !== undefined)
              resolvedField = "tradePriceQty";
            else if (totalVolume !== undefined) resolvedField = "totalVolume";
            else if (bidPrice !== undefined) resolvedField = "bidPrice";
            else if (offerPrice !== undefined) resolvedField = "offerPrice";
            else if (bidSize !== undefined) resolvedField = "bidSize";
            else if (offerSize !== undefined) resolvedField = "offerSize";
          }

          if (resolvedField) {
            const isPrice = isPriceField(resolvedField);
            const triggeredVal = payloadObject[resolvedField];

            const formattedLimit =
              limitValue !== undefined ? limitValue : "[Target]";
            const formattedVal = isPrice
              ? typeof triggeredVal === "number"
                ? triggeredVal.toFixed(2)
                : triggeredVal !== undefined
                  ? triggeredVal
                  : "[Value]"
              : triggeredVal !== undefined
                ? new Intl.NumberFormat().format(triggeredVal)
                : "[Value]";

            let readableField = "";
            if (resolvedField === "bidPrice") readableField = "bid price";
            else if (
              resolvedField === "bidSize" ||
              resolvedField === "bidPriceQty"
            )
              readableField = "bid qty";
            else if (resolvedField === "offerPrice")
              readableField = "offer price";
            else if (
              resolvedField === "offerSize" ||
              resolvedField === "offerPriceQty"
            )
              readableField = "offer qty";
            else if (
              resolvedField === "totalVolume" ||
              resolvedField === "tradeVolume"
            )
              readableField = "volume";
            else readableField = getFieldLabel(resolvedField).toLowerCase();

            let title = "";
            let body = "";
            const opSym = getOperatorSymbol(operator);

            if (resolvedField === "tradePrice") {
              title = `${alertName} traded at price ${formattedVal}`;
            } else if (resolvedField === "tradePriceQty") {
              title = `${alertName} traded with quantity ${formattedVal}`;
            } else if (
              resolvedField === "totalVolume" ||
              resolvedField === "tradeVolume"
            ) {
              title = `${alertName} total ${readableField} is ${formattedVal}`;
            } else {
              title = `${alertName} ${readableField} is ${formattedVal}`;
            }

            if (opSym === ">" || opSym === ">=") {
              body = `Above your target of ${formattedLimit}`;
            } else if (opSym === "<" || opSym === "<=") {
              body = `Below your target of ${formattedLimit}`;
            } else if (opSym === "=") {
              body = `Equals your target of ${formattedLimit}`;
            } else {
              body = `Reached your target of ${formattedLimit}`;
            }

            toastMessage = `${title}\n${body}`;
          }
        }
      }

      if (toastMessage) {
        PubSub.publish(
          pubsubEvents.successKeys.ALERT_NOTIFICATION_TOAST,
          toastMessage,
        );
      } else {
        console.warn(
          "Received alert notification with missing or unknown data:",
          data,
        );
      }
    } catch (e) {
      console.error("Error processing alert notification:", e, data);
    }
  }
}
